import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChildProfile } from '../../models/childProfile';
import { ORG_CONFIG_DEFAULTS } from '../../models/orgConfig';
import ChildSelect from '../ChildSelect';

interface Props {
  open: boolean;
  childProfiles: ChildProfile[];
  /** Used for sensible time defaults. */
  now: Date;
  /** Slot length in minutes — drives default rounding + the time-input
   *  step. Comes from the org config; falls back to the safe default
   *  while config is loading. */
  sessionIntervalMin?: number;
  onConfirm: (data: {
    childId: number;
    startTime: string;     // 'HH:MM'
    endTime: string;       // 'HH:MM'
  }) => void;
  onCancel: () => void;
}

function roundDownToInterval(d: Date, intervalMin: number): string {
  const mins = Math.floor((d.getHours() * 60 + d.getMinutes()) / intervalMin) * intervalMin;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function addMinutes(time: string, mins: number): string {
  const [h, m] = time.split(':').map(Number);
  const total = (h * 60 + m + mins) % (24 * 60);
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

const UnscheduledSessionModal: React.FC<Props> = ({
  open, childProfiles, now,
  sessionIntervalMin = ORG_CONFIG_DEFAULTS.defaultSessionIntervalMinutes,
  onConfirm, onCancel,
}) => {
  const [childId, setChildId] = useState<number | ''>('');
  const [startTime, setStartTime] = useState<string>(
    () => roundDownToInterval(now, sessionIntervalMin),
  );
  const [endTime, setEndTime] = useState<string>(
    () => addMinutes(roundDownToInterval(now, sessionIntervalMin), sessionIntervalMin),
  );

  // Reset the form when the modal OPENS — on that edge only.
  //
  // `now` is a dependency because the fresh times are seeded from it, but the
  // dashboard ticks it every 20 seconds to keep its countdowns honest. Without
  // the edge check this effect fired on every tick, so a modal left open long
  // enough to read lost the child you had just picked and snapped the times
  // back to the clock, roughly every 20 seconds, for no reason the user could
  // see.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      const start = roundDownToInterval(now, sessionIntervalMin);
      setChildId('');
      setStartTime(start);
      setEndTime(addMinutes(start, sessionIntervalMin));
    }
    wasOpen.current = open;
  }, [open, now, sessionIntervalMin]);

  const sortedChildren = useMemo(
    () => [...childProfiles].sort((a, b) =>
      `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`),
    ),
    [childProfiles],
  );

  if (!open) return null;

  const valid = childId !== '' && startTime && endTime && startTime < endTime;

  const handleConfirm = () => {
    if (!valid) return;
    onConfirm({ childId, startTime, endTime });
  };

  const labelStyle: React.CSSProperties = {
    fontSize: '0.72rem',
    color: '#5a6a58',
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    display: 'block',
    marginBottom: 4,
  };

  return (
    <div
      className="modal show d-block"
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-labelledby="unsched-title"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div
          className="modal-content ivory-panel"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="modal-header" style={{ position: 'relative', zIndex: 1 }}>
            <h5
              id="unsched-title"
              className="modal-title d-flex align-items-center gap-2"
              style={{ fontWeight: 600 }}
            >
              <i className="fas fa-plus" style={{ color: '#26923f', fontSize: '0.95rem' }} aria-hidden />
              Add unscheduled session
            </h5>
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={onCancel}
            />
          </div>

          <div className="modal-body" style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ marginBottom: 12 }} data-help="dash.unscheduled-child">
              <ChildSelect
                childProfiles={sortedChildren}
                value={childId === '' ? null : childId}
                onChange={(id) => setChildId(id ?? '')}
                placeholder="Choose a child"
                autoFocus
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <label style={labelStyle}>Start</label>
                <input
                  type="time"
                  className="form-control form-control-sm"
                  step={sessionIntervalMin * 60}
                  value={startTime}
                  onChange={(e) => {
                    setStartTime(e.target.value);
                    // Auto-bump end if it would be invalid.
                    if (e.target.value >= endTime) setEndTime(addMinutes(e.target.value, sessionIntervalMin));
                  }}
                />
              </div>
              <div>
                <label style={labelStyle}>End</label>
                <input
                  type="time"
                  className="form-control form-control-sm"
                  step={sessionIntervalMin * 60}
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                />
              </div>
            </div>

            {!valid && childId !== '' && startTime >= endTime && (
              <div style={{ color: '#b8302f', fontSize: '0.78rem', marginTop: 10 }}>
                End time must be after start time.
              </div>
            )}
          </div>

          <div className="modal-footer" style={{ position: 'relative', zIndex: 1 }}>
            <button type="button" className="btn btn-secondary" onClick={onCancel}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary d-inline-flex align-items-center gap-2"
              data-help="dash.unscheduled-add"
              onClick={handleConfirm}
              disabled={!valid}
            >
              <i className="fas fa-plus" aria-hidden />
              Add session
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnscheduledSessionModal;
